import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Sparkles, Search, Users as UsersIcon } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { MultiSelect } from '@/components/ui/multi-select';
import { useAllUsersUIPreferences } from '@/hooks/useUserUIPreferences';
import { useAppUsers } from '@/hooks/useAppUsers';
import { toast } from 'sonner';

const PAGE_OPTIONS = [
  { value: 'leads', label: 'Leads' },
  { value: 'deals', label: 'Deals' },
  { value: 'cases', label: 'Cases' },
  { value: 'contacts', label: 'Contacts' },
  { value: 'dashboard', label: 'Dashboard' },
];

export default function UIPersonalizationSettings() {
  const [searchQuery, setSearchQuery] = useState('');
  const { users, isLoading: usersLoading } = useAppUsers();
  const { allPreferences, isLoading: prefsLoading, updatePreferences } = useAllUsersUIPreferences();

  // Combine users with their preferences
  const usersWithPrefs = React.useMemo(() => {
    if (!users) return [];
    
    return users.map(user => {
      const prefs = allPreferences?.find(p => p.user_email === user.email);
      return {
        ...user,
        preferences: prefs || {
          enable_falling_petals: false,
          enable_header_flowers: false,
          pages_with_animations: [],
        }
      };
    });
  }, [users, allPreferences]);

  // Filter users based on search
  const filteredUsers = usersWithPrefs.filter(user => {
    if (!searchQuery) return true;
    const search = searchQuery.toLowerCase();
    return (
      user.email?.toLowerCase().includes(search) ||
      user.first_name?.toLowerCase().includes(search) ||
      user.last_name?.toLowerCase().includes(search) ||
      user.display_name?.toLowerCase().includes(search)
    );
  });

  const handleTogglePetals = async (userEmail, enabled) => {
    try {
      await updatePreferences.mutateAsync({
        userEmail,
        updates: {
          enable_falling_petals: enabled,
        }
      });
      
      toast.success('Atualizado!', {
        description: `Pétalas ${enabled ? 'ativadas' : 'desativadas'} para ${userEmail}`,
      });
    } catch (error) {
      toast.error('Erro', {
        description: error.message || 'Falha ao atualizar preferências',
      });
    }
  };

  const handleToggleFlowers = async (userEmail, enabled) => {
    try {
      await updatePreferences.mutateAsync({
        userEmail,
        updates: {
          enable_header_flowers: enabled,
        }
      });
      
      toast.success('Atualizado!', {
        description: `Flores ${enabled ? 'ativadas' : 'desativadas'} para ${userEmail}`,
      });
    } catch (error) {
      toast.error('Erro', {
        description: error.message || 'Falha ao atualizar preferências',
      });
    }
  };

  const handleUpdatePages = async (userEmail, pages) => {
    try {
      await updatePreferences.mutateAsync({
        userEmail,
        updates: {
          pages_with_animations: pages,
        }
      });
      
      toast.success('Atualizado!', {
        description: `Páginas atualizadas para ${userEmail}`,
      });
    } catch (error) {
      toast.error('Erro', {
        description: error.message || 'Falha ao atualizar páginas',
      });
    }
  };

  const handleEnableForAll = async () => {
    if (!confirm('Ativar animações para todos os usuários?')) return;
    
    try {
      for (const user of usersWithPrefs) {
        await updatePreferences.mutateAsync({
          userEmail: user.email,
          updates: {
            enable_falling_petals: true,
            enable_header_flowers: true,
            pages_with_animations: ['leads'],
          }
        });
      }
      
      toast.success('Sucesso!', {
        description: 'Animações ativadas para todos os usuários',
      });
    } catch (error) {
      toast.error('Erro', {
        description: error.message || 'Falha ao ativar para todos',
      });
    }
  };

  const handleDisableForAll = async () => {
    if (!confirm('Disable all animations for all users?')) return;
    
    try {
      for (const user of usersWithPrefs) {
        await updatePreferences.mutateAsync({
          userEmail: user.email,
          updates: {
            enable_falling_petals: false,
            enable_header_flowers: false,
            pages_with_animations: [],
          }
        });
      }
      
      toast.success('Sucesso!', {
        description: 'Animações desativadas para todos os usuários',
      });
    } catch (error) {
      toast.error('Erro', {
        description: error.message || 'Falha ao desativar para todos',
      });
    }
  };

  const handleRemoveAll = async (userEmail) => {
    if (!confirm(`Remove all animations for ${userEmail}?`)) return;
    
    try {
      await updatePreferences.mutateAsync({
        userEmail,
        updates: {
          enable_falling_petals: false,
          enable_header_flowers: false,
          pages_with_animations: [],
        }
      });
      
      toast.success('Removido!', {
        description: `Animações removidas para ${userEmail}`,
      });
    } catch (error) {
      toast.error('Erro', {
        description: error.message || 'Falha ao remover configurações',
      });
    }
  };

  if (usersLoading || prefsLoading) {
    return (
      <div className="bg-background p-6 md:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-muted rounded w-1/4"></div>
            <div className="h-64 bg-muted rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background p-6 md:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2 flex items-center gap-2">
            <Sparkles className="h-8 w-8 text-primary" />
            Personalização de Interface
          </h1>
          <p className="text-muted-foreground">
            Gerencie animações e efeitos visuais para usuários específicos
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Controle de Animações por Usuário</CardTitle>
            <CardDescription>
              Configure quais usuários visualizam as animações decorativas (pétalas e flores) e em quais páginas
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Filters and Actions */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por nome ou email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              <div className="flex gap-2">
                <Button 
                  onClick={handleEnableForAll} 
                  variant="default"
                  disabled={updatePreferences.isPending}
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  Ativar para Todos
                </Button>
                <Button 
                  onClick={handleDisableForAll} 
                  variant="outline"
                  disabled={updatePreferences.isPending}
                >
                  Desativar para Todos
                </Button>
              </div>
            </div>

            {/* Users Table */}
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Usuário</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead className="text-center">Pétalas</TableHead>
                    <TableHead className="text-center">Flores</TableHead>
                    <TableHead className="min-w-[200px]">Páginas</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        <UsersIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                        <p>Nenhum usuário encontrado</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredUsers.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell className="font-medium">
                          {user.display_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Sem nome'}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {user.email}
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex justify-center">
                            <Switch
                              checked={user.preferences.enable_falling_petals}
                              onCheckedChange={(checked) => 
                                handleTogglePetals(user.email, checked)
                              }
                              disabled={updatePreferences.isPending}
                            />
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex justify-center">
                            <Switch
                              checked={user.preferences.enable_header_flowers}
                              onCheckedChange={(checked) => 
                                handleToggleFlowers(user.email, checked)
                              }
                              disabled={updatePreferences.isPending}
                            />
                          </div>
                        </TableCell>
                        <TableCell>
                          <MultiSelect
                            options={PAGE_OPTIONS}
                            value={user.preferences.pages_with_animations || []}
                            onChange={(pages) => handleUpdatePages(user.email, pages)}
                            placeholder="Selecione páginas..."
                            emptyText="Nenhuma página encontrada"
                          />
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleRemoveAll(user.email)}
                            disabled={updatePreferences.isPending}
                          >
                            Remover Todos
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
